import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { IPayment } from '@/modules/payment/types';
import axiosClient from '@/shared/lib/axios';

/************************************ UPDATE EMAIL *********************************************/
export const updateEmail = async (email?: string) => {
  const { data } = await axiosClient.put(`/company/portalInsights/user-email`, {
    email,
  });
  return data;
};

export const useUpdateEmail = () => {
  return useMutation({
    mutationKey: ['updateEmail'],
    mutationFn: updateEmail,
  });
};

/************************************ SEND EMAIL VERIFICATION *********************************************/
export const sendEmailVerification = async (email?: string) => {
  const { data } = await axiosClient.post(
    `/company/portalInsights/send-email-verification`,
    { email }
  );
  return data;
};

export const useSendEmailVerification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['sendEmailVerification'],
    mutationFn: sendEmailVerification,
    onSuccess: () => {
      queryClient.invalidateQueries(['getUserInfo']);
    },
  });
};

/************************************ VERIFY EMAIL *********************************************/
export const verifyEmail = async (pin: string) => {
  const { data } = await axiosClient.post(
    `/company/portalInsights/verify-email`,
    { pin }
  );
  return data;
};

export const useVerifyEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['verifyEmail'],
    mutationFn: verifyEmail,
    onSuccess: () => {
      queryClient.invalidateQueries(['getUserInfo']);
    },
  });
};

/************************************ SUBSCRIBING STATUS *********************************************/
export const subscriptionStatus = async (subscriptionStatus: string) => {
  const { data } = await axiosClient.post(
    `/company/portalInsights/subscriptions/status`,
    { subscriptionStatus }
  );
  return data;
};

export const useSubscriptionStatus = () => {
  return useMutation({
    mutationKey: ['subscriptionStatus'],
    mutationFn: subscriptionStatus,
  });
};
/************************************ FINALIZE SUBSCRIBING TO A PLAN *********************************************/
export const finalizePlanSubscription = async (params: {
  priceId: string;
  checkoutSessionId: string;
}) => {
  const { data } = await axiosClient.post(
    `/company/portalInsights/subscriptions/subscribe-finalize`,
    { ...params }
  );
  return data;
};

export const useFinalizePlanSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['finalizePlanSubscription'],
    mutationFn: finalizePlanSubscription,
    onSuccess: () => {
      queryClient.invalidateQueries(['getUserInfo']);
      queryClient.invalidateQueries(['getEntities']);
    },
  });
};
/************************************ SUBSCRIBE TO A PLAN *********************************************/
export const subscribePlan = async (
  priceId: string
): Promise<{ url: string }> => {
  const { data } = await axiosClient.post(
    `/company/portalInsights/subscriptions/subscribe`,
    { priceId }
  );
  return data;
};

export const useSubscribePlan = () => {
  return useMutation({
    mutationKey: ['subscribePlan'],
    mutationFn: subscribePlan,
  });
};
/************************************ CANCEL PLAN *********************************************/
export const cancelPlan = async () => {
  const { data } = await axiosClient.delete(
    `/company/portalInsights/billing/cancel-subscription`
  );
  return data;
};

export const useCancelPlan = () => {
  return useMutation({
    mutationKey: ['cancelPlan'],
    mutationFn: cancelPlan,
  });
};
/************************************ GET INVOICES *********************************************/
export const getInvoices = async (): Promise<IPayment.IInvoicesListSchema> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/billing/all-invoices`
  );
  return data;
};

export const useGetInvoices = () => {
  return useQuery({
    queryKey: ['getInvoices'],
    queryFn: getInvoices,
  });
};
/************************************ GET FIELDS *********************************************/
export const getFields = async (): Promise<IPayment.IFieldsSchema> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/billing/company-fields`
  );
  return data;
};

export const useGetFields = () => {
  return useQuery({
    queryKey: ['getFields'],
    queryFn: getFields,
  });
};
/************************************ PAYMENT METHOD *********************************************/
export const getPaymentMethod =
  async (): Promise<IPayment.IPaymentMethodSchema> => {
    const { data } = await axiosClient.get(
      `/company/portalInsights/billing/payment-method`
    );
    return data;
  };

export const useGetPaymentMethod = () => {
  return useQuery({
    queryKey: ['getPaymentMethod'],
    queryFn: getPaymentMethod,
  });
};
/************************************ SUBSCRIBED PLAN *********************************************/
export const getSubscribePlan =
  async (): Promise<IPayment.ISubscriptionPlan> => {
    const { data } = await axiosClient.get(
      `/company/portalInsights/billing/subscription-plan`
    );
    return data;
  };

export const useGetSubscribePlan = () => {
  return useQuery({
    queryKey: ['getSubscribePlan'],
    queryFn: getSubscribePlan,
  });
};
/************************************ UPCOMING INVOICE *********************************************/
export const getUpcomingInvoice = async (): Promise<IPayment.IInvoice> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/billing/upcoming-invoice`
  );
  return data;
};

export const useGetUpcomingInvoice = () => {
  return useQuery({
    queryKey: ['getUpcomingInvoice'],
    queryFn: getUpcomingInvoice,
  });
};
/************************************ PAYMENT PLANS *********************************************/
export const getPaymentBundles = async (): Promise<
  IPayment.IBundleSchema[]
> => {
  const { data } = await axiosClient.get(
    `/company/portalInsights/subscriptions/plans`
  );
  return data;
};

export const useGetPaymentBundles = () => {
  return useQuery({
    queryKey: ['getPaymentBundles'],
    queryFn: getPaymentBundles,
  });
};
/************************************ UPDATE PAYMENT METHOD *********************************************/
export const updatePaymentMethod = async () => {
  const { data } = await axiosClient.put(
    `/company/portalInsights/billing/payment-method`
  );
  return data;
};

export const useUpdatePaymentMethod = () => {
  return useMutation({
    mutationKey: ['updatePaymentMethod'],
    mutationFn: updatePaymentMethod,
  });
};
/************************************ CHANGE SUBCRIPTION *********************************************/
export const changeSubcription = async (priceId: string) => {
  const { data } = await axiosClient.put(
    `/company/portalInsights/billing/change-subscription`,
    {
      priceId,
    }
  );
  return data;
};

export const useChangeSubcription = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['changeSubcription'],
    mutationFn: changeSubcription,
    onSuccess: () => {
      queryClient.invalidateQueries(['getUserInfo']);
    },
  });
};
